import React, { useEffect, useMemo, useState } from "react";
import { formatSubjectName } from "../../../layouts/schoolPortal/schoolPortalTheme";
import {
  Dialog, Box, Typography, Button, TextField, MenuItem, IconButton, CircularProgress, Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { useSnackbar } from "notistack";
import { set_student_homework_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import {
  bg, surface, surfaceRaised, border,
  textPrimary, textSecondary, textMuted, accent, accentSoft, font,
  success, dialogPaperSx, primaryButtonSx, secondaryButtonSx, inputSx, easing, shadow,
} from "../../../layouts/schoolPortal/schoolPortalTheme";
import { humaniseTopic } from "./recordTheme";

/**
 * Setting work, like a checkout.
 *
 * HOW IT GOT HERE
 * A stack of nine-control cards (unreadable). A tap-a-row picker (clever, three
 * verbs, you had to already know what the rows meant). A form behind a dropdown
 * (you could not see what the tool could do). A three-step wizard (you could
 * see everything, but four items meant walking 1-2-3 four times).
 *
 * The wizard was the wrong shape for the job. Setting homework is shopping: you
 * browse a menu, drop things in a basket as you go, and pay once at the end.
 * You do not check out separately for each item.
 *
 * SO:
 *   the menu    every kind of work, visible, grouped, always on screen
 *   the basket  pinned at the bottom, filling up as you add
 *   checkout    once, at the end — deadline, a line from you, send
 *
 * Adding never leaves the menu. A row opens in place, asks for the two or three
 * things it needs, and folds shut again once it is in the basket.
 *
 * HIERARCHY IS TONE, NOT HAIRLINES.
 * Ground is `bg`, cards are `surface`, header and basket are `surfaceRaised`.
 * Three planes, so a card reads as a card and the eye lands on the white rows.
 *
 * NO SCORES, NO STATS.
 * An earlier version put percentages on every subject and topic, and required a
 * pass mark on some kinds of work. Both are gone: they made the menu read as a
 * report, and the number an instructor needs is on the record behind it.
 */

const atTime = (d, h, m = 0) => { const x = new Date(d); x.setHours(h, m, 0, 0); return x; };
const plusHours = (h) => new Date(Date.now() + h * 3600000);
const plusDays = (n) => { const x = new Date(); x.setDate(x.getDate() + n); return x; };

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * The real date, spelled out next to the relative one.
 *
 * "In a week" alone makes an instructor work out what that lands on, and "Fri
 * 12 Sep" alone makes them work out how long that gives the student. Both, and
 * neither question gets asked.
 */
const describeDue = (d) => {
  const now = new Date();
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay(d, now)) return `${time} today`;
  if (sameDay(d, plusDays(1))) return `${time} tomorrow`;
  return `${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}, ${time}`;
};

/**
 * Deadlines worth offering, from three hours to a fortnight.
 *
 * WHY THE SHORT END EXISTS
 * "Twenty questions before your lesson at six" is a real instruction and the
 * commonest kind of homework there is — the old list started at Friday, so the
 * only way to say it was not to. Everything carries a time, not just a date,
 * because "by Thursday" and "before Thursday's lesson" are different deadlines.
 *
 * Anything already past is dropped rather than offered and rejected: at 8pm,
 * "tonight" is not a deadline.
 */
const dueChoices = () => {
  const now = new Date();

  const friday = new Date(now);
  // The end of this working week — or next week's, if today is Friday or later.
  const delta = (5 - friday.getDay() + 7) % 7 || 7;
  friday.setDate(friday.getDate() + delta);

  const raw = [
    { label: "In 3 hours", when: plusHours(3) },
    { label: "In 6 hours", when: plusHours(6) },
    { label: "Tonight", when: atTime(now, 21) },
    { label: "Tomorrow", when: atTime(plusDays(1), 18) },
    { label: "In 2 days", when: atTime(plusDays(2), 18) },
    { label: "In 5 days", when: atTime(plusDays(5), 18) },
    { label: "By Friday", when: atTime(friday, 18) },
    { label: "In a week", when: atTime(plusDays(7), 18) },
    { label: "In two weeks", when: atTime(plusDays(14), 18) },
  ];

  const seen = new Set();
  return raw
    .filter((o) => o.when > now)
    .filter((o) => {
      // "By Friday" and "Tomorrow" are the same day on a Thursday. Two rows
      // that set the identical deadline is a choice that is not a choice.
      const k = o.when.toISOString();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .map((o) => ({ label: o.label, value: o.when.toISOString(), hint: describeDue(o.when) }));
};

/** The value a `datetime-local` input wants, in local time rather than UTC. */
const toLocalInput = (d) => {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
};

const COUNTS = [10, 20, 30, 50, 100];

/** What each size actually asks of an evening. */
const COUNT_BLURB = {
  10: "A quick check",
  20: "A sitting",
  30: "A proper go",
  50: "A long session",
  100: "Split over a few days",
};

/**
 * EVERY KIND OF WORK, IN ONE TABLE.
 *
 * `needs` is what the row asks for when it opens, and nothing else is rendered.
 * `tracked` says whether the server can close the task by counting real
 * answers — printed on the row, because "review the last session" being
 * uncheckable is a bad thing to discover afterwards.
 *
 * Adding a kind of work is a row here and nothing else.
 */
const WORK = [
  {
    group: "Answering questions",
    groupNote: "XAir counts these — the task closes itself",
    icon: <QuizOutlinedIcon sx={{ fontSize: 15 }} />,
    tone: "#c01e2e",
    types: [
      { id: "practise_subject", label: "Practise a subject",
        blurb: "A batch of questions across the whole subject.",
        needs: ["subject", "count"], targetKind: "questions", defaultCount: 30, tracked: true },
      { id: "practise_topic", label: "Practise one topic",
        blurb: "The same, narrowed to a single topic.",
        needs: ["subject", "topic"], targetKind: "questions", defaultCount: 20, tracked: true, alsoCount: true },
      { id: "redo_topic", label: "Redo a topic",
        blurb: "Go back over one they have already been through.",
        needs: ["subject", "topic"], targetKind: "questions", defaultCount: 20, tracked: true, alsoCount: true },
      { id: "mock", label: "Mock paper",
        blurb: "A full-length run at one subject. Not timed — XAir does not hold a clock.",
        needs: ["subject", "count"], targetKind: "questions", defaultCount: 50, tracked: true },
    ],
  },
  {
    group: "Reading and Learn",
    groupNote: "No counter — they tick these off",
    icon: <MenuBookOutlinedIcon sx={{ fontSize: 15 }} />,
    tone: "#2f6fae",
    types: [
      { id: "learn_topic", label: "Work through a topic in Learn",
        blurb: "Read it properly rather than answering questions on it.",
        needs: ["subject", "topic"], targetKind: "topic_review", tracked: false },
      { id: "finish_subject", label: "Finish a subject they have started",
        blurb: "For the ones left half-done.",
        needs: ["subject"], targetKind: "topic_review", tracked: false },
    ],
  },
  {
    group: "Around the lesson",
    groupNote: "No counter — they tick these off",
    icon: <EventNoteOutlinedIcon sx={{ fontSize: 15 }} />,
    tone: "#3e8f6b",
    types: [
      { id: "review_session", label: "Review the last session",
        blurb: "The summary and the recording, while it is still fresh.",
        needs: [], needsLastSession: true,
        targetKind: null, tracked: false },
      { id: "prep_next", label: "Prepare for the next lesson",
        blurb: "Name what to read before they turn up.",
        needs: ["subject", "topic?"], targetKind: "topic_review", tracked: false },
      { id: "bring_questions", label: "Bring questions to the next lesson",
        blurb: "Write down what did not make sense and bring the list.",
        needs: [], fixedTitle: "Write down anything that did not make sense and bring it to your next lesson",
        targetKind: null, tracked: false },
    ],
  },
  {
    group: "Anything else",
    groupNote: "Nothing to do with questions or Learn",
    icon: <MoreHorizOutlinedIcon sx={{ fontSize: 15 }} />,
    tone: "#8a8580",
    types: [
      { id: "custom", label: "Something else",
        blurb: "Medical, paperwork, a document to read.",
        needs: ["text"], targetKind: null, tracked: false },
    ],
  },
];

const ALL_TYPES = WORK.flatMap((g) => g.types);
const typeOf = (id) => ALL_TYPES.find((t) => t.id === id) || null;
const wants = (t, k) => Boolean(t?.needs?.includes(k) || t?.needs?.includes(`${k}?`) || (k === "count" && t?.alsoCount));
const requires = (t, k) => Boolean(t?.needs?.includes(k));

const titleFor = (item) => {
  const type = typeOf(item.typeId);
  if (item.text) return item.text;
  if (type?.fixedTitle) return type.fixedTitle;
  // Narrowest first: the student should read the thing they were actually set.
  const where = item.microTopic
    ? humaniseTopic(item.microTopic)
    : item.topic ? humaniseTopic(item.topic) : item.subjectLabel;
  if (type?.id === "learn_topic") return `Work through ${where} in Learn`;
  if (type?.id === "finish_subject") return `Finish ${where}`;
  if (type?.id === "prep_next") return `Read up on ${where} before your next lesson`;
  if (type?.id === "redo_topic") return `Redo ${where}, ${item.count} questions`;
  if (type?.id === "mock") return `Mock paper: ${item.count} questions on ${where}`;
  return `${item.count} questions on ${where}`;
};

const groupLabelSx = {
  fontFamily: font, fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textSecondary,
};

const badgeSx = (tracked) => ({
  px: 0.7, py: 0.15, borderRadius: "4px", flexShrink: 0,
  fontFamily: font, fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.04em",
  textTransform: "uppercase", whiteSpace: "nowrap",
  color: tracked ? success : textSecondary,
  backgroundColor: tracked ? `${success}14` : surfaceRaised,
});

const fieldSx = { ...inputSx, backgroundColor: surface, borderRadius: "8px" };

/**
 * Every dropdown in here scrolls.
 *
 * Without a cap, MUI sizes the menu Paper to its contents, and inside a Dialog
 * that meant a subject with 27 topics rendered a menu taller than the viewport
 * with no way to reach the bottom of it — the list looked truncated rather than
 * scrollable, so half the syllabus was simply unreachable.
 */
const menuProps = {
  PaperProps: {
    sx: {
      maxHeight: "min(60vh, 480px)",
      // Momentum scrolling on a trackpad/touch, and a scrollbar that is
      // actually visible against the white menu rather than a ghost.
      overscrollBehavior: "contain",
      "&::-webkit-scrollbar": { width: 8 },
      "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.18)", borderRadius: 4 },
    },
  },
  // Anchored to the field rather than the selected item, so opening a long list
  // does not jump the menu over the control you just clicked.
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

/**
 * A deadline: relative label on the left, the date it actually lands on to the
 * right, and a custom picker for anything the list does not cover.
 */
function DeadlineField({ value, onChange, label, helper, emptyLabel }) {
  const choices = useMemo(() => dueChoices(), []);
  const isPreset = value && choices.some((c) => c.value === value);
  const custom = Boolean(value) && !isPreset;

  return (
    <>
      <TextField
        select size="small" SelectProps={{ MenuProps: menuProps }} fullWidth label={label}
        value={custom ? "pick" : (value || "")}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "pick") {
            // Seed the picker with something sensible rather than an empty
            // field the instructor has to fill from scratch.
            onChange(value || atTime(plusDays(1), 18).toISOString());
          } else {
            onChange(v);
          }
        }}
        helperText={helper}
        FormHelperTextProps={{ sx: { fontFamily: font, fontSize: "10.5px", color: textMuted, ml: 0.5 } }}
        sx={{ ...fieldSx, mb: custom ? 1 : 1.25 }}
      >
        <MenuItem value="" sx={{ fontFamily: font, fontSize: "13px" }}>{emptyLabel}</MenuItem>
        {choices.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ display: "flex", alignItems: "baseline", gap: 1.5, py: 0.7 }}>
            <Typography sx={{
              flex: 1, fontFamily: font, fontSize: "13px", fontWeight: 600, color: textPrimary,
            }}>
              {o.label}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, whiteSpace: "nowrap" }}>
              {o.hint}
            </Typography>
          </MenuItem>
        ))}
        <MenuItem value="pick" sx={{ fontFamily: font, fontSize: "13px" }}>Pick a date and time…</MenuItem>
      </TextField>

      {custom && (
        <TextField
          size="small" fullWidth type="datetime-local"
          label="Due" value={toLocalInput(value)}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : "")}
          InputLabelProps={{ shrink: true }}
          helperText={describeDue(new Date(value))}
          FormHelperTextProps={{ sx: { fontFamily: font, fontSize: "10.5px", color: textMuted, ml: 0.5 } }}
          sx={{ ...fieldSx, mb: 1.25 }}
        />
      )}
    </>
  );
}

export default function HomeworkModal({ open, onClose, onDone, record, studentId }) {
  const { enqueueSnackbar } = useSnackbar();

  const subjects = useMemo(() => record?.coverage?.subjects || [], [record]);
  const pace = record?.coverage?.pace || null;
  // Counted work needs a question bank to count against. The FAA course has
  // none yet, so for a US student those rows are shown locked with the reason
  // (the server refuses them too) rather than set and never finishable.
  const practiceLocked = record?.coverage?.practice?.available === false;
  const practiceReason = record?.coverage?.practice?.reason || "Practice questions aren't available for this course yet.";
  const firstName = (record?.student?.name || "").split(" ")[0] || "them";

  const [openType, setOpenType] = useState(null);
  const [draft, setDraft] = useState({});
  const [items, setItems] = useState([]);
  const [checkout, setCheckout] = useState(false);
  const [duePreset, setDuePreset] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOpenType(null); setDraft({}); setItems([]);
    setCheckout(false); setDuePreset(""); setNote("");
  }, [open]);

  const type = openType ? typeOf(openType) : null;

  const toggle = (t) => {
    if (openType === t.id) { setOpenType(null); return; }
    setOpenType(t.id);
    setDraft({ subject: "", topic: "", microTopic: "", text: "", count: t.defaultCount ?? 20, body: "", dueAt: "", showNote: false, showDue: false });
  };

  const subjectRow = subjects.find((s) => s.subject === draft.subject) || null;

  /**
   * The session "review the last session" actually means.
   *
   * Naming it matters: "review the last session" arrives in a student's inbox
   * with no date, no subject and nothing to open, and a task you cannot act on
   * without asking which one is a task nobody does. If there is no past session
   * the option is not offered at all, rather than sending them to look for
   * something that does not exist.
   */
  const lastSession = useMemo(() => {
    const now = Date.now();
    return (record?.timeline || [])
      .filter((e) => e.kind === "session"
        && e.at && new Date(e.at).getTime() < now
        && e.status !== "cancelled"
        && !e.noShow)
      .sort((a, b) => new Date(b.at) - new Date(a.at))[0] || null;
  }, [record]);

  const lastSessionTitle = useMemo(() => {
    if (!lastSession) return null;
    const when = new Date(lastSession.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const what = lastSession.subject ? `${formatSubjectName(lastSession.subject)} session` : "session";
    // Only promise a recording when there is one to open.
    const material = lastSession.hasRecording
      ? "read the summary and watch the recording back"
      : "read the summary back";
    return `Review your ${what} from ${when} — ${material}`;
  }, [lastSession]);

  /**
   * Every topic in the subject, not just the ones they have already answered.
   *
   * `coverage.topics` is built by aggregating the student's own attempts, so a
   * topic they have never opened is absent from it — which is exactly the topic
   * you most want to be able to set. `allTopics` is the syllabus list and is
   * the one to offer from; the answered list is only used to fill in the gaps
   * for a subject whose syllabus we could not resolve.
   */
  /**
   * Every topic in the subject, at the level questions are actually tagged.
   *
   * `topicTree` is the syllabus, two deep: a broad grouping ("wind") with the
   * micro topics underneath it ("geostrophic wind", "katabatic and anabatic").
   * The micro list is the real one — 126 for air law against 27 broad — so
   * offering only the broad names put most of the syllabus out of reach.
   *
   * Each group also gets an "All of …" row, because "practise wind" and
   * "practise the geostrophic wind" are both things an instructor sets, and
   * the first counts every micro topic under it.
   */
  const topicOptions = useMemo(() => {
    if (!subjectRow) return [];
    const out = [];
    for (const branch of subjectRow.topicTree || []) {
      const group = humaniseTopic(branch.topic);
      out.push({
        key: `${branch.topic}::`, broad: branch.topic, micro: "",
        label: `All of ${group}`, group, covers: (branch.micro || []).length,
      });
      for (const m of branch.micro || []) {
        out.push({ key: `${branch.topic}::${m}`, broad: branch.topic, micro: m, label: humaniseTopic(m), group });
      }
    }
    // Older API without the tree: fall back to what they have answered, so the
    // field degrades to the previous behaviour rather than to nothing.
    if (!out.length) {
      for (const t of subjectRow.topics || []) {
        if (!t.topic || t.topic === "all") continue;
        out.push({ key: `${t.topic}::`, broad: t.topic, micro: "", label: humaniseTopic(t.topic), group: "Topics" });
      }
    }
    return out;
  }, [subjectRow]);

  const selectedTopic = topicOptions.find(
    (o) => o.broad === (draft.topic || "") && o.micro === (draft.microTopic || ""),
  ) || null;

  const canAdd = useMemo(() => {
    if (!type) return false;
    if (type.needsLastSession && !lastSession) return false;
    if (requires(type, "text")) return Boolean(draft.text?.trim());
    if (requires(type, "subject") && !draft.subject) return false;
    if (requires(type, "topic") && !draft.topic) return false;
    if (wants(type, "count") && !(draft.count > 0)) return false;
    return true;
  }, [type, draft, lastSession]);

  const addItem = () => {
    if (!canAdd || !type) return;
    setItems((prev) => [...prev, {
      key: `${type.id}:${draft.subject}:${draft.topic}:${Date.now()}`,
      typeId: type.id, tracked: type.tracked, targetKind: type.targetKind,
      subject: draft.subject || "", subjectLabel: subjectRow?.label || "",
      topic: draft.topic || "",
      microTopic: draft.microTopic || "",
      count: wants(type, "count") ? draft.count : null,
      text: type.needsLastSession ? (lastSessionTitle || "") : (draft.text?.trim() || ""),
      // Optional, per item. The set-level pair at checkout are the defaults;
      // anything set here overrides them for this one task only.
      body: draft.body?.trim() || "",
      dueAt: draft.dueAt || "",
    }]);
    // Fold shut and stay exactly where they are. Adding a fourth thing must not
    // cost the same four clicks as the first.
    setOpenType(null);
    setDraft({});
  };

  const totals = useMemo(() => {
    const questions = items.reduce((n, i) => n + (i.count || 0), 0);
    const minutes = pace?.medianSeconds ? (questions * pace.medianSeconds) / 60 : null;
    return { questions, minutes };
  }, [items, pace]);

  const dueAt = duePreset;

  const submit = async () => {
    if (!items.length) return;
    setBusy(true);
    try {
      const r = await set_student_homework_api(studentId, {
        items: items.map((i) => ({
          title: titleFor(i),
          body: i.body || "",
          // createHomeworkSet falls back to the set-level dueAt when this is
          // absent, so an empty string here means "use whatever checkout says".
          dueAt: i.dueAt || null,
          // No target at all for the ticked-off ones. The server accepts that —
          // only a COUNTED target has to name a subject.
          target: i.targetKind ? {
            kind: i.targetKind, subject: i.subject || null,
            topic: i.topic || null, microTopic: i.microTopic || null,
            count: i.count, minAccuracy: null,
          } : null,
        })),
        dueAt: dueAt || null,
        note: note.trim(),
      });
      if (r?.code === 200) {
        enqueueSnackbar(`Sent to ${firstName} — ${items.length} item${items.length === 1 ? "" : "s"}, and they've been emailed`,
          { variant: "success" });
        onDone?.(); onClose?.();
      } else {
        enqueueSnackbar(r?.message || "Could not set that work", { variant: "error" });
      }
    } catch (e) {
      enqueueSnackbar("Could not set that work", { variant: "error" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog
      open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"
      PaperProps={{
        sx: {
          ...dialogPaperSx,
          backgroundColor: bg,
          overflow: "hidden",
          maxWidth: { xs: 560, sm: 620 },
          // A floor, not a fixed height: short content still shrinks, but the
          // scrolling region stops being a letterbox on a big screen.
          minHeight: { xs: 0, sm: 620 },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "92vh", minHeight: 0 }}>

        {/* ── header ───────────────────────────────────────────────────────── */}
        <Box sx={{
          backgroundColor: surfaceRaised, borderBottom: `1px solid ${border}`,
          px: { xs: 1.75, sm: 2.25 }, py: 1.5, flexShrink: 0,
          display: "flex", alignItems: "flex-start", gap: 1,
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: font, fontSize: "16px", fontWeight: 800, color: textPrimary, lineHeight: 1.3 }}>
              {checkout ? "Ready to send" : `Set work for ${firstName}`}
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.1 }}>
              {checkout
                ? `${items.length} item${items.length === 1 ? "" : "s"} — they get one email with the list`
                : "Add as many as you like, then send them together."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={busy} sx={{ color: textMuted, mt: 0.25 }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ── body ─────────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 1.75, sm: 2.25 }, py: 1.75 }}>

          {!checkout && WORK.map((group) => (
            <Box key={group.group} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, mb: 0.85, px: 0.25 }}>
                <Box sx={{
                  width: 24, height: 24, borderRadius: "7px", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  backgroundColor: `${group.tone}14`, color: group.tone,
                }}>
                  {group.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...groupLabelSx, color: textPrimary, lineHeight: 1.2 }}>
                    {group.group}
                  </Typography>
                  {group.groupNote && (
                    <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, lineHeight: 1.3 }}>
                      {group.groupNote}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{
                borderRadius: "12px", overflow: "hidden", boxShadow: shadow,
                borderLeft: `3px solid ${group.tone}`,
              }}>
                {group.types.map((t, i) => {
                  const isOpen = openType === t.id;
                  const locked = (t.needsLastSession && !lastSession) || (t.tracked && practiceLocked);
                  return (
                    <Box key={t.id} sx={{ borderTop: i === 0 ? "none" : `1px solid ${border}` }}>
                      <Box
                        onClick={() => { if (!locked) toggle(t); }}
                        sx={{
                          display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1.15,
                          cursor: locked ? "not-allowed" : "pointer",
                          opacity: locked ? 0.5 : 1,
                          backgroundColor: isOpen ? `${group.tone}0f` : surface,
                          transition: `background-color 0.12s ${easing}`,
                          "&:hover": { backgroundColor: `${group.tone}0f` },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontFamily: font, fontSize: "13.5px", fontWeight: 700,
                            color: isOpen ? group.tone : textPrimary,
                          }}>
                            {t.label}
                          </Typography>
                          <Typography sx={{
                            fontFamily: font, fontSize: "11.5px", color: textSecondary,
                            mt: 0.1, lineHeight: 1.45,
                          }}>
                            {t.needsLastSession
                              ? (lastSession
                                  ? lastSessionTitle.replace(/^Review your /, "Their last: ").replace(/ — .*$/, "")
                                  : "No past sessions on record yet — nothing to review.")
                              : (t.tracked && practiceLocked ? practiceReason : t.blurb)}
                          </Typography>
                        </Box>
                        <Box sx={badgeSx(t.tracked)}>{t.tracked ? "Tracked" : "Ticked"}</Box>
                        <AddIcon sx={{
                          fontSize: 17, flexShrink: 0,
                          color: isOpen ? group.tone : textMuted,
                          transform: isOpen ? "rotate(45deg)" : "none",
                          transition: `transform 0.15s ${easing}`,
                        }} />
                      </Box>

                      {isOpen && (
                        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, backgroundColor: `${group.tone}0f` }}>
                          {t.needsLastSession && lastSession && (
                            <Typography sx={{
                              fontFamily: font, fontSize: "12px", color: textPrimary,
                              backgroundColor: surface, border: `1px solid ${border}`,
                              borderRadius: "8px", px: 1.25, py: 0.9, mb: 1.25, lineHeight: 1.5,
                            }}>
                              {lastSessionTitle}
                            </Typography>
                          )}

                          {wants(t, "subject") && (
                            <TextField
                              select size="small" SelectProps={{ MenuProps: menuProps }} fullWidth label="Subject" value={draft.subject || ""}
                              onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value, topic: "", microTopic: "" }))}
                              sx={{ ...fieldSx, mb: 1.25 }}
                            >
                              {subjects.map((s) => {
                                const groups = (s.topicTree || []).length;
                                const micro = (s.topicTree || []).reduce((n, b) => n + (b.micro || []).length, 0);
                                return (
                                  <MenuItem key={s.subject} value={s.subject} sx={{ display: "block", py: 0.8 }}>
                                    <Typography sx={{
                                      fontFamily: font, fontSize: "13px", fontWeight: 600,
                                      color: textPrimary, lineHeight: 1.3,
                                    }}>
                                      {s.label}
                                    </Typography>
                                    {micro > 0 && (
                                      <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>
                                        {groups} group{groups === 1 ? "" : "s"} · {micro} topics
                                      </Typography>
                                    )}
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                          )}

                          {wants(t, "topic") && draft.subject && (
                            <Autocomplete
                              size="small"
                              options={topicOptions}
                              value={selectedTopic}
                              groupBy={(o) => o.group}
                              getOptionLabel={(o) => o.label}
                              isOptionEqualToValue={(a, b) => a.key === b.key}
                              disabled={!topicOptions.length}
                              onChange={(e, v) => setDraft((d) => ({
                                ...d, topic: v?.broad || "", microTopic: v?.micro || "",
                              }))}
                              // A hundred-odd options is past the point a
                              // dropdown works: this one is typed at.
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={requires(t, "topic") ? "Topic" : "Topic (optional)"}
                                  placeholder="Type to search"
                                  helperText={topicOptions.length
                                    ? `${topicOptions.length} to choose from${requires(t, "topic") ? "" : " — leave blank for the whole subject"}`
                                    : "No topics recorded on this subject yet"}
                                  FormHelperTextProps={{ sx: { fontFamily: font, fontSize: "10.5px", color: textMuted, ml: 0.5 } }}
                                  sx={fieldSx}
                                />
                              )}
                              ListboxProps={{ style: { maxHeight: "min(58vh, 460px)", paddingTop: 0 } }}
                              slotProps={{
                                paper: {
                                  sx: {
                                    borderRadius: "10px",
                                    border: `1px solid ${border}`,
                                    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                                    mt: 0.5,
                                  },
                                },
                              }}
                              /* The group heading stays put while its own
                                 topics scroll under it — with 126 options in
                                 nine groups, a heading that scrolls away leaves
                                 you reading a list of names with no idea which
                                 part of the subject you are in. */
                              renderGroup={(params) => (
                                <li key={params.key}>
                                  <Box sx={{
                                    position: "sticky", top: 0, zIndex: 1,
                                    backgroundColor: surfaceRaised,
                                    borderBottom: `1px solid ${border}`,
                                    px: 1.5, py: 0.55,
                                  }}>
                                    <Typography sx={{
                                      fontFamily: font, fontSize: "10px", fontWeight: 800,
                                      letterSpacing: "0.07em", textTransform: "uppercase",
                                      color: textSecondary,
                                    }}>
                                      {params.group}
                                    </Typography>
                                  </Box>
                                  <Box component="ul" sx={{ p: 0, m: 0, listStyle: "none" }}>
                                    {params.children}
                                  </Box>
                                </li>
                              )}
                              renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                  <Box
                                    component="li" key={option.key} {...rest}
                                    sx={{
                                      display: "flex", alignItems: "center", gap: 1,
                                      px: 1.5, py: 0.7,
                                      // The whole-group row leads its group and
                                      // is a different kind of choice, so it is
                                      // weighted and the micro rows are indented
                                      // beneath it rather than sitting level.
                                      pl: option.micro ? 2.75 : 1.5,
                                      borderBottom: option.micro ? "none" : `1px solid ${border}`,
                                    }}
                                  >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography sx={{
                                        fontFamily: font, fontSize: "12.5px",
                                        fontWeight: option.micro ? 400 : 700,
                                        color: textPrimary, lineHeight: 1.35,
                                      }}>
                                        {option.label}
                                      </Typography>
                                      {!option.micro && (
                                        <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>
                                          Counts every topic in this group
                                        </Typography>
                                      )}
                                    </Box>
                                    {!option.micro && option.covers > 0 && (
                                      <Box sx={{
                                        px: 0.7, py: 0.1, borderRadius: "4px", flexShrink: 0,
                                        backgroundColor: accentSoft, color: accent,
                                        fontFamily: font, fontSize: "10px", fontWeight: 800,
                                      }}>
                                        {option.covers}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              }}
                              sx={{ mb: 1.25 }}
                            />
                          )}

                          {wants(t, "text") && (
                            <TextField
                              size="small" fullWidth multiline minRows={3} autoFocus
                              label="What should they do?" value={draft.text || ""}
                              onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                              placeholder="Book your Class 2 medical and send us the certificate."
                              sx={{ ...fieldSx, mb: 1.25 }}
                            />
                          )}

                          {wants(t, "count") && draft.subject && (
                            <TextField
                              select size="small" SelectProps={{ MenuProps: menuProps }} fullWidth label="How many questions" value={draft.count}
                              onChange={(e) => setDraft((d) => ({ ...d, count: e.target.value }))}
                              sx={{ ...fieldSx, mb: 1.25 }}
                            >
                              {COUNTS.map((n) => (
                                <MenuItem key={n} value={n} sx={{ display: "block", py: 0.7 }}>
                                  <Typography sx={{
                                    fontFamily: font, fontSize: "13px", fontWeight: 600,
                                    color: textPrimary, lineHeight: 1.3,
                                  }}>
                                    {n} questions
                                  </Typography>
                                  <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted }}>
                                    {COUNT_BLURB[n]}
                                  </Typography>
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          {/* Both optional and both shut by default: most
                              items need neither, and a note box on every row is
                              what made an earlier version unreadable. */}
                          <Box sx={{ display: "flex", gap: 0.5, mb: draft.showNote || draft.showDue ? 1 : 1.25 }}>
                            {!draft.showNote && (
                              <Button
                                onClick={() => setDraft((d) => ({ ...d, showNote: true }))}
                                sx={{ ...secondaryButtonSx, minHeight: 28, fontSize: "11.5px", px: 1.25 }}
                              >
                                + Note
                              </Button>
                            )}
                            {!draft.showDue && (
                              <Button
                                onClick={() => setDraft((d) => ({ ...d, showDue: true }))}
                                sx={{ ...secondaryButtonSx, minHeight: 28, fontSize: "11.5px", px: 1.25 }}
                              >
                                + Deadline
                              </Button>
                            )}
                          </Box>

                          {draft.showNote && (
                            <TextField
                              size="small" fullWidth multiline minRows={2} autoFocus
                              label="Note for this one (optional)"
                              value={draft.body || ""}
                              onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                              placeholder="Focus on the decoding rather than the theory."
                              sx={{ ...fieldSx, mb: 1.25 }}
                            />
                          )}

                          {draft.showDue && (
                            <DeadlineField
                              label="Deadline for this one"
                              emptyLabel="Use the one set at the end"
                              helper="Miss it and they show as falling behind on the board"
                              value={draft.dueAt || ""}
                              onChange={(v) => setDraft((d) => ({ ...d, dueAt: v }))}
                            />
                          )}

                          <Button
                            onClick={addItem} disabled={!canAdd}
                            sx={{ ...primaryButtonSx, width: "100%", minHeight: 34,
                                  "&.Mui-disabled": { opacity: 0.4, color: "#fff" } }}
                          >
                            Add to the list
                          </Button>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}

          {checkout && (
            <>
              <Typography sx={{ ...groupLabelSx, mb: 0.75, px: 0.25 }}>Deadline &amp; message</Typography>
              <Box sx={{
                backgroundColor: surface, borderRadius: "12px", boxShadow: shadow,
                border: `1px solid ${border}`, p: 1.75,
              }}>
                <DeadlineField
                  label={items.some((i) => i.dueAt) ? "Deadline for the rest" : "Deadline"}
                  emptyLabel="No deadline"
                  helper={items.some((i) => i.dueAt)
                    ? "Items with their own deadline keep it"
                    : "Optional — miss it and they show as falling behind on the board"}
                  value={duePreset}
                  onChange={setDuePreset}
                />
                <TextField
                  size="small" fullWidth multiline minRows={2} label="A line from you (optional)"
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="This is the one that keeps catching you out — have a proper go before Thursday."
                  sx={fieldSx}
                />
              </Box>
            </>
          )}
        </Box>

        {/* ── the basket: pinned, so it fills up in front of you ────────────── */}
        <Box sx={{
          backgroundColor: surfaceRaised, borderTop: `1px solid ${border}`,
          px: { xs: 1.75, sm: 2.25 }, py: 1.25, flexShrink: 0,
        }}>
          {items.length > 0 && (
            <Box sx={{ maxHeight: 150, overflowY: "auto", mb: 1.25 }}>
              {items.map((item, i) => (
                <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: 0.9, py: 0.4 }}>
                  <Box sx={{
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: accentSoft, color: accent, fontFamily: font,
                    fontSize: "10px", fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{i + 1}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary }}>
                      {titleFor(item)}
                    </Typography>
                    {(item.dueAt || item.body) && (
                      <Typography noWrap sx={{ fontFamily: font, fontSize: "10.5px", color: textMuted, mt: 0.1 }}>
                        {item.dueAt ? `due ${describeDue(new Date(item.dueAt))}` : ""}
                        {item.dueAt && item.body ? " · " : ""}
                        {item.body ? "has a note" : ""}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={badgeSx(item.tracked)}>{item.tracked ? "Tracked" : "Ticked"}</Box>
                  <IconButton
                    size="small"
                    onClick={() => setItems((prev) => prev.filter((p) => p.key !== item.key))}
                    sx={{ color: textMuted, flexShrink: 0 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{
              flex: 1, fontFamily: font, fontSize: "11.5px",
              color: items.length ? textSecondary : textMuted,
            }}>
              {items.length
                ? `${items.length} item${items.length === 1 ? "" : "s"}`
                  + (totals.questions ? ` · ${totals.questions} questions` : "")
                  + (totals.minutes != null ? ` · about ${Math.round(totals.minutes)} min` : "")
                : "Nothing added yet"}
            </Typography>

            {checkout ? (
              <>
                <Button onClick={() => setCheckout(false)} disabled={busy} sx={secondaryButtonSx}>
                  Add more
                </Button>
                <Button
                  onClick={submit} disabled={busy || !items.length}
                  sx={{ ...primaryButtonSx, minWidth: 132, "&.Mui-disabled": { opacity: 0.4, color: "#fff" } }}
                >
                  {busy ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : `Send to ${firstName}`}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose} disabled={busy} sx={secondaryButtonSx}>Cancel</Button>
                <Button
                  onClick={() => setCheckout(true)} disabled={!items.length}
                  sx={{ ...primaryButtonSx, minWidth: 132, "&.Mui-disabled": { opacity: 0.4, color: "#fff" } }}
                >
                  Review &amp; send
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
